import React, { useState, useEffect, useCallback } from 'react';
import {
  METRICS_DATA,
  MILESTONES,
  FACILITIES,
  REGIONS,
  SURGICAL_INQUIRIES_SINCE_LABEL,
} from './data/constants.js';
import type { MetricScopeKey, TimeFilterKey } from './data/constants.js';
import { formatNum, normalizeSurgeonName } from './data/helpers.js';
import { buildMissionControlExport, downloadMissionControlExport } from './data/export.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { missionControlService, LEGACY_INQUIRY_GROUP_ID } from '../../services/missionControl.js';
import type { DashboardData } from '../../services/missionControl.js';
import { Panel } from './components/Panel.js';
import { LiveClock } from './components/LiveClock.js';
import { StatusIndicator } from './components/StatusIndicator.js';
import { GlobeMap } from './components/GlobeMap.js';
import { MetricCard } from './components/MetricCard.js';
import type { MetricBreakdownRow } from './components/MetricCard.js';
import { TimeFilter } from './components/TimeFilter.js';
import { MetricScopeFilter } from './components/MetricScopeFilter.js';
import { PatentFlywheel, PatentDetail } from './components/PatentFlywheel.js';
import { MilestoneBar } from './components/MilestoneBar.js';
import { FacilityCard } from './components/FacilityCard.js';
import { ProductIntel } from './components/ProductIntel.js';
import { FacilitySettings } from './components/FacilitySettings.js';
import { MilestoneSettings } from './components/MilestoneSettings.js';
import { ProductIntelSettings } from './components/ProductIntelSettings.js';
import exexLogo from '../../assets/straightened_exex_gradient.png';
import './mission-control.css';

export function MissionControlPage(): React.ReactElement {
  const [timeFilter, setTimeFilter] = useState<TimeFilterKey>('all');
  const [metricScope, setMetricScope] = useState<MetricScopeKey>('withLegacy');
  const [selectedPatent, setSelectedPatent] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');
  const [expandedFacility, setExpandedFacility] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(
    () => localStorage.getItem('mc-fullscreen') === 'true'
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [milestoneSettingsOpen, setMilestoneSettingsOpen] = useState(false);
  const [productIntelSettingsOpen, setProductIntelSettingsOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const { hasRole } = useAuth();

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      localStorage.setItem('mc-fullscreen', String(next));
      if (next) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      } else {
        document.exitFullscreen?.().catch(() => {});
      }
      return next;
    });
  }, []);

  // Sync state when user exits fullscreen via Escape key
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
        localStorage.setItem('mc-fullscreen', 'false');
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [isFullscreen]);

  // Re-enter browser fullscreen on mount if persisted
  useEffect(() => {
    if (isFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch live dashboard data from API
  const fetchDashboard = useCallback(() => {
    missionControlService.getDashboardData().then((result) => {
      if (result.success && result.data) {
        setDashboardData(result.data);
      }
    });
  }, []);

  // Initial fetch + 60-second polling
  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 60_000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const isLiveDataLoading = !dashboardData;
  // Reaches only the metric cards below — the directory, the milestones, and
  // Product Intel are commercial-only whichever way the toggle is set, so the
  // default cards deliberately read higher than the directory beneath them.
  const isWithLegacy = metricScope === 'withLegacy';
  const metricsData =
    (isWithLegacy ? dashboardData?.metricsWithLegacy : dashboardData?.metrics) ?? METRICS_DATA;
  const milestones = dashboardData?.milestones ?? MILESTONES;
  const activeFacilities = dashboardData?.facilities ?? FACILITIES;
  const metrics = metricsData[timeFilter];

  // Empty while loading and on the fallback payload — the card then shows its plain tooltip.
  const inquirySource = isWithLegacy
    ? dashboardData?.inquiriesByFacilityWithLegacy
    : dashboardData?.inquiriesByFacility;
  const inquiryRows = inquirySource?.[timeFilter] ?? [];
  const inquiryBreakdown: MetricBreakdownRow[] = inquiryRows.flatMap((row) => {
    if (row.facilityId !== LEGACY_INQUIRY_GROUP_ID) {
      return [{ id: row.facilityId, label: row.facilityName, value: formatNum(row.count) }];
    }
    // The roll-up is itemized underneath: the aggregate alone doesn't say which
    // legacy facilities it came from, which is the useful part of the number.
    const legacyRows = row.legacyFacilities ?? [];
    return [
      {
        id: row.facilityId,
        label: `${row.facilityName} (${legacyRows.length} ${legacyRows.length === 1 ? 'facility' : 'facilities'})`,
        value: formatNum(row.count),
        divider: true,
      },
      ...legacyRows.map((facility) => ({
        id: `${row.facilityId}::${facility.facilityId}`,
        label: facility.facilityName,
        value: formatNum(facility.count),
        sub: true,
      })),
    ];
  });
  const usStatus =
    dashboardData?.firebaseStatus?.us?.status === 'connected'
      ? 'ONLINE'
      : isLiveDataLoading
        ? 'ONLINE'
        : 'OFFLINE';
  const ukStatus =
    dashboardData?.firebaseStatus?.uk?.status === 'connected'
      ? 'ONLINE'
      : isLiveDataLoading
        ? 'ONLINE'
        : 'OFFLINE';

  const handleExport = () => {
    const payload = buildMissionControlExport(dashboardData, new Date().toISOString());
    downloadMissionControlExport(payload);
  };

  const filteredFacilities = (
    regionFilter === 'all'
      ? activeFacilities
      : activeFacilities.filter((f) => f.region === regionFilter)
  ).sort((a, b) => a.name.localeCompare(b.name));

  const allSurgeons = filteredFacilities.flatMap((f) =>
    f.surgeons.map((s) => ({ ...s, facility: f.name, facilityId: f.id }))
  );
  const uniqueSurgeonCount = new Set(allSurgeons.map((s) => normalizeSurgeonName(s.name))).size;
  const totalDirectoryProfiles = allSurgeons.reduce((a, s) => a + s.profiles, 0);
  const totalDirectoryRecorded = allSurgeons.reduce((a, s) => a + s.recorded, 0);
  const allSpecialties = [...new Set(allSurgeons.map((s) => s.specialty))].sort();

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const fadeClass = (delay?: number) => {
    const base = 'mc-fade-in';
    const visible = mounted ? ' mc-fade-in--visible' : '';
    const style = delay ? { transitionDelay: `${delay}s` } : undefined;
    return { className: `${base}${visible}`, style };
  };

  const compositeScore = (
    milestones.reduce((acc, m) => acc + Math.min(m.current / m.target, 1) * m.weight, 0) * 100
  ).toFixed(0);

  return (
    <div className={`mc-page${isFullscreen ? ' mc-page--fullscreen' : ''}`}>
      {/* Background grid */}
      <div className="mc-bg-grid" />

      {/* Scanline effect */}
      <div className="mc-scanline">
        <div className="mc-scanline__beam" />
      </div>

      {/* Corner accent lines */}
      <div className="mc-corner mc-corner--top-left-h" />
      <div className="mc-corner mc-corner--top-left-v" />
      <div className="mc-corner mc-corner--top-right-h" />
      <div className="mc-corner mc-corner--top-right-v" />

      {/* Content */}
      <div className="mc-content">
        {/* ── Header ──────────────────────────────────────── */}
        <div {...fadeClass(0)}>
          {isFullscreen && (
            <div className="mc-header-fullscreen">
              <span className="mc-header__title">Mission Control</span>
            </div>
          )}
          <div className={`mc-header${isFullscreen ? ' mc-header--fullscreen' : ''}`}>
            <div>
              <div className="mc-header__brand">
                <img src={exexLogo} alt="eXeX" className="mc-header__logo" />
                {!isFullscreen && (
                  <>
                    <div className="mc-header__divider" />
                    <span className="mc-header__title">Mission Control</span>
                  </>
                )}
              </div>
              <LiveClock />
            </div>
            <div className="mc-header__right">
              <div className="mc-header__right-top">
                <div className="mc-header__classification">Classification: Confidential</div>
                <button
                  className="mc-header__fullscreen-btn"
                  onClick={handleExport}
                  title="Export dashboard data (JSON)"
                  aria-label="Export dashboard data"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 10V2m0 0L5.5 4.5M8 2l2.5 2.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.5 7.5H3a1 1 0 00-1 1V13a1 1 0 001 1h10a1 1 0 001-1V8.5a1 1 0 00-1-1h-.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {hasRole(['mc-admin', 'mc-editor']) && (
                  <button
                    className="mc-header__fullscreen-btn"
                    onClick={() => setSettingsOpen(true)}
                    title="Facility Settings"
                    aria-label="Facility Settings"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 10a2 2 0 100-4 2 2 0 000 4z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                      />
                      <path
                        d="M13.5 8c0-.3-.2-.6-.4-.8l1-1.6-.8-1.4-1.8.4c-.4-.3-.8-.6-1.3-.7L9.8 2H8.2l-.4 1.9c-.5.1-.9.4-1.3.7l-1.8-.4-.8 1.4 1 1.6c-.1.2-.2.5-.2.8s.1.6.2.8l-1 1.6.8 1.4 1.8-.4c.4.3.8.6 1.3.7l.4 1.9h1.6l.4-1.9c.5-.1.9-.4 1.3-.7l1.8.4.8-1.4-1-1.6c.2-.2.3-.5.3-.8z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                <button
                  className="mc-header__fullscreen-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <div className="mc-header__status-row">
                <StatusIndicator label="US-CENTRAL" status={usStatus} />
                <StatusIndicator label="EU-LONDON" status={ukStatus} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Main Grid ───────────────────────────────────── */}
        <div className="mc-grid">
          {/* Left Column: Infrastructure + Metrics */}
          <div className="mc-column">
            <div {...fadeClass(0.1)}>
              <Panel title="Global Infrastructure">
                <GlobeMap usStatus={usStatus} ukStatus={ukStatus} />
              </Panel>
            </div>
            <div {...fadeClass(0.2)}>
              <Panel
                title="Operational Metrics"
                headerRight={
                  <div className="mc-panel__controls">
                    <MetricScopeFilter selected={metricScope} onChange={setMetricScope} />
                    <TimeFilter selected={timeFilter} onChange={setTimeFilter} />
                  </div>
                }
                overflowVisible
              >
                <div className="mc-metrics-grid">
                  <MetricCard
                    label="Surgeons"
                    value={formatNum(metrics.surgeons)}
                    sub="Onboarded"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="Facilities"
                    value={metrics.facilities}
                    sub="Active"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="ORs"
                    value={metrics.ors}
                    sub="Connected"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="Profiles"
                    value={formatNum(metrics.profiles)}
                    sub="Surgical"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="Recorded"
                    value={formatNum(metrics.recorded)}
                    sub="Procedures"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="Inquiries"
                    value={formatNum(metrics.inquiries)}
                    sub="Surgical"
                    info={
                      timeFilter === 'all'
                        ? `Tracking since ${SURGICAL_INQUIRIES_SINCE_LABEL}`
                        : undefined
                    }
                    breakdown={inquiryBreakdown}
                    breakdownLabel="By facility"
                    loading={isLiveDataLoading}
                  />
                  <MetricCard
                    label="Staff"
                    value={formatNum(metrics.staff)}
                    sub="Techs & Nurses"
                    loading={isLiveDataLoading}
                  />
                </div>
              </Panel>
            </div>
          </div>

          {/* Center Column: Patent Flywheel */}
          <div {...fadeClass(0.15)}>
            <div className="mc-column">
              <Panel
                title="IP Portfolio"
                headerRight={
                  <div className="mc-ip-count">
                    <div className="mc-ip-count__number">7</div>
                    <div className="mc-ip-count__label">
                      PATENTS
                      <br />
                      GRANTED
                    </div>
                  </div>
                }
              >
                <PatentFlywheel selected={selectedPatent} onSelect={setSelectedPatent} />
                <PatentDetail selectedIndex={selectedPatent} />
              </Panel>
            </div>
          </div>

          {/* Right Column: Series A Milestones + System Intel */}
          <div {...fadeClass(0.25)}>
            <div className="mc-column">
              <Panel
                title="Series A Readiness"
                headerRight={
                  hasRole(['mc-admin', 'mc-editor']) ? (
                    <button
                      className="mc-header__fullscreen-btn"
                      onClick={() => setMilestoneSettingsOpen(true)}
                      title="Milestone Settings"
                      aria-label="Milestone Settings"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M8 10a2 2 0 100-4 2 2 0 000 4z"
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                        <path
                          d="M13.5 8c0-.3-.2-.6-.4-.8l1-1.6-.8-1.4-1.8.4c-.4-.3-.8-.6-1.3-.7L9.8 2H8.2l-.4 1.9c-.5.1-.9.4-1.3.7l-1.8-.4-.8 1.4 1 1.6c-.1.2-.2.5-.2.8s.1.6.2.8l-1 1.6.8 1.4 1.8-.4c.4.3.8.6 1.3.7l.4 1.9h1.6l.4-1.9c.5-.1.9-.4 1.3-.7l1.8.4.8-1.4-1-1.6c.2-.2.3-.5.3-.8z"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : undefined
                }
              >
                <div className="mc-milestone-banner">
                  <div className="mc-milestone-banner__dot" />
                  <span className="mc-milestone-banner__text">TRACKING TO ROADSHOW</span>
                </div>
                {milestones.map((m) => (
                  <MilestoneBar key={m.label} milestone={m} loading={isLiveDataLoading} />
                ))}
                <div className="mc-milestone-composite">
                  <span className="mc-milestone-composite__label">COMPOSITE SCORE</span>
                  <span
                    className={`mc-milestone-composite__value${isLiveDataLoading ? ' loading-blur' : ''}`}
                  >
                    {compositeScore}%
                  </span>
                </div>
              </Panel>
            </div>
          </div>
        </div>

        {/* ── Product Intel (full-width) ────────────────────── */}
        <div className={`mc-directory ${fadeClass(0.3).className}`} style={fadeClass(0.3).style}>
          <Panel
            title="Product Intel"
            headerRight={
              hasRole(['mc-admin', 'mc-editor']) ? (
                <button
                  className="mc-header__fullscreen-btn"
                  onClick={() => setProductIntelSettingsOpen(true)}
                  title="Product Intel Settings"
                  aria-label="Product Intel Settings"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 10a2 2 0 100-4 2 2 0 000 4z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M13.5 8c0-.3-.2-.6-.4-.8l1-1.6-.8-1.4-1.8.4c-.4-.3-.8-.6-1.3-.7L9.8 2H8.2l-.4 1.9c-.5.1-.9.4-1.3.7l-1.8-.4-.8 1.4 1 1.6c-.1.2-.2.5-.2.8s.1.6.2.8l-1 1.6.8 1.4 1.8-.4c.4.3.8.6 1.3.7l.4 1.9h1.6l.4-1.9c.5-.1.9-.4 1.3-.7l1.8.4.8-1.4-1-1.6c.2-.2.3-.5.3-.8z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : undefined
            }
          >
            <ProductIntel
              products={dashboardData?.productIntel}
              activeProcedures={dashboardData?.activeProcedures}
              loading={isLiveDataLoading}
            />
          </Panel>
        </div>

        {/* ── Commercial Facility & Surgeon Directory ─────────────────── */}
        <div className={`mc-directory ${fadeClass(0.35).className}`} style={fadeClass(0.35).style}>
          <Panel
            title="Commercial Facility & Surgeon Directory"
            headerRight={
              <div className="mc-time-filter">
                {REGIONS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => {
                      setRegionFilter(r.key);
                      setExpandedFacility(null);
                    }}
                    className={`mc-time-filter__btn${regionFilter === r.key ? ' mc-time-filter__btn--active' : ''}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            }
          >
            {/* Summary stats bar */}
            <div className="mc-directory__summary">
              <div className="mc-directory__stat">
                <span
                  className={`mc-directory__stat-value${isLiveDataLoading ? ' loading-blur' : ''}`}
                >
                  {filteredFacilities.length}
                </span>
                <span className="mc-directory__stat-label">FACILITIES</span>
              </div>
              <div className="mc-directory__divider" />
              <div className="mc-directory__stat">
                <span
                  className={`mc-directory__stat-value${isLiveDataLoading ? ' loading-blur' : ''}`}
                >
                  {uniqueSurgeonCount}
                </span>
                <span className="mc-directory__stat-label">SURGEONS</span>
              </div>
              <div className="mc-directory__divider" />
              <div className="mc-directory__stat">
                <span
                  className={`mc-directory__stat-value mc-directory__stat-value--accent${isLiveDataLoading ? ' loading-blur' : ''}`}
                >
                  {formatNum(totalDirectoryProfiles)}
                </span>
                <span className="mc-directory__stat-label">SURGICAL PROFILES</span>
              </div>
              <div className="mc-directory__divider" />
              <div className="mc-directory__stat">
                <span
                  className={`mc-directory__stat-value mc-directory__stat-value--accent${isLiveDataLoading ? ' loading-blur' : ''}`}
                >
                  {formatNum(totalDirectoryRecorded)}
                </span>
                <span className="mc-directory__stat-label">RECORDED PROCEDURES</span>
              </div>
              <div className="mc-directory__divider" />
              <div className="mc-directory__specialties">
                {allSpecialties.map((s) => (
                  <span key={s} className="mc-directory__specialty-tag">
                    {s.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>

            {/* Facility cards */}
            <div className="mc-directory__facilities">
              {filteredFacilities.map((facility) => (
                <FacilityCard
                  key={facility.id}
                  facility={facility}
                  isExpanded={expandedFacility === facility.id}
                  onToggle={() =>
                    setExpandedFacility(expandedFacility === facility.id ? null : facility.id)
                  }
                  loading={isLiveDataLoading}
                />
              ))}
            </div>
          </Panel>
        </div>

        {/* ── Footer ──────────────────────────────────────── */}
        <div {...fadeClass(0.4)}>
          <div className="mc-footer">
            <span className="mc-footer__text">
              eXeX — eXPANDED eXISTENCE — INTERNAL USE ONLY — CONFIDENTIAL
            </span>
            <span className="mc-footer__text mc-footer__text--right">THE FUTURE OF SURGERY</span>
          </div>
        </div>
      </div>

      {hasRole(['mc-admin', 'mc-editor']) && (
        <FacilitySettings
          isOpen={settingsOpen}
          onClose={() => {
            setSettingsOpen(false);
            fetchDashboard();
          }}
        />
      )}
      {hasRole(['mc-admin', 'mc-editor']) && (
        <MilestoneSettings
          isOpen={milestoneSettingsOpen}
          onClose={() => {
            setMilestoneSettingsOpen(false);
            fetchDashboard();
          }}
        />
      )}
      {hasRole(['mc-admin', 'mc-editor']) && (
        <ProductIntelSettings
          isOpen={productIntelSettingsOpen}
          onClose={() => {
            setProductIntelSettingsOpen(false);
            fetchDashboard();
          }}
        />
      )}
    </div>
  );
}
